import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Pagination,
  Skeleton,
  Grid,
  Menu,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ErrorPage from "../Components/ErrorPage";
import useFetchObjects from "../../api/useFetchObjects";
import useDelete from "../../api/useDelete";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import PassiveIncomeAdd from "./PassiveIncomeAdd";
import PassiveIncomeEdit from "./PassiveIncomeEdit";
import { printPassiveIncome } from "../utils/printPassiveIncome";

const PassiveIncomeIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState(null);
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [actionRow, setActionRow] = useState(null);

  const { data, isLoading, isError, refetch } = useFetchObjects(
    ["passive-incomes", searchTerm, page, rowsPerPage],
    `shop/passive-incomes/?search=${searchTerm}&page=${page}&page_size=${rowsPerPage}`,
    token
  );

  useEffect(() => {
    refetch();
  }, [searchTerm, page, rowsPerPage, refetch]);

  const { handleDelete, ConfirmDialog } = useDelete("shop/passive-incomes", token, refetch);

  const rows = data?.results ?? data ?? [];
  const totalCount = data?.count ?? rows.length;

  const closeActionMenu = () => {
    setActionAnchorEl(null);
    setActionRow(null);
  };

  if (isError) {
    return <ErrorPage message={t("passiveIncome.failedToLoad", "Failed to load passive incomes.")} />;
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h4">{t("passiveIncome.title", "Passive Income")}</Typography>
        <Button variant="contained" onClick={() => setOpenAdd(true)}>
          {t("passiveIncome.addNew", "Add New")}
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <TextField
          variant="outlined"
          fullWidth
          placeholder={t("passiveIncome.searchPlaceholder", "Search passive income...")}
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table aria-label="passive income table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "primary.main" }}>
              <TableCell sx={{ color: "white" }}>#</TableCell>
              <TableCell sx={{ color: "white" }}>{t("passiveIncome.branchName", "Branch Name")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("passiveIncome.name", "Name")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("passiveIncome.account", "Account")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("passiveIncome.currency", "Currency")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("passiveIncome.amount", "Amount")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("passiveIncome.exchangeRate", "Exchange Rate")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("passiveIncome.convertedAmount", "Converted Amount")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("passiveIncome.incomeDate", "Income Date")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("passiveIncome.notes", "Notes")}</TableCell>
              <TableCell sx={{ color: "white" }}>{t("common.action", "Action")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from(new Array(5)).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from(new Array(11)).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : rows.length > 0
              ? rows.map((row, i) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{(page - 1) * rowsPerPage + i + 1}</TableCell>
                    <TableCell>{row.shop_details?.name || row.shop_name || "-"}</TableCell>
                    <TableCell>{row.name || "-"}</TableCell>
                    <TableCell>{row.account_details?.name || "-"}</TableCell>
                    <TableCell>{row.currency_details?.title || "-"}</TableCell>
                    <TableCell>{row.amount}</TableCell>
                    <TableCell>{row.exchange_rate}</TableCell>
                    <TableCell>{row.converted_amount}</TableCell>
                    <TableCell>
                      {row.income_date ? new Date(row.income_date).toLocaleString() : "-"}
                    </TableCell>
                    <TableCell>{row.notes || "-"}</TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setActionAnchorEl(e.currentTarget);
                          setActionRow(row);
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              : (
                  <TableRow>
                    <TableCell colSpan={11} align="center">
                      {t("common.noData", "No data found")}
                    </TableCell>
                  </TableRow>
                )}
          </TableBody>
        </Table>
      </TableContainer>

      <Grid container justifyContent="center" alignItems="center" sx={{ p: 2 }}>
        <Pagination
          count={Math.ceil(totalCount / rowsPerPage) || 1}
          page={page}
          onChange={(e, value) => setPage(value)}
          variant="outlined"
          color="primary"
        />
      </Grid>

      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl && actionRow)}
        onClose={closeActionMenu}
      >
        <MenuItem
          onClick={() => {
            if (!actionRow) return;
            setEditData(actionRow);
            setOpenEdit(true);
            closeActionMenu();
          }}
        >
          {t("common.edit", "Edit")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!actionRow) return;
            handleDelete(actionRow.id);
            closeActionMenu();
          }}
        >
          {t("common.delete", "Delete")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (!actionRow) return;
            printPassiveIncome(actionRow);
            closeActionMenu();
          }}
        >
          {t("common.print", "Print")}
        </MenuItem>
      </Menu>

      <ConfirmDialog />

      {openAdd && (
        <PassiveIncomeAdd
          open={openAdd}
          onClose={() => setOpenAdd(false)}
          token={token}
          onSuccess={refetch}
        />
      )}

      {openEdit && editData && (
        <PassiveIncomeEdit
          open={openEdit}
          onClose={() => {
            setOpenEdit(false);
            setEditData(null);
          }}
          editData={editData}
          token={token}
          onSuccess={refetch}
        />
      )}
    </Box>
  );
};

export default PassiveIncomeIndex;
